import React, { useState, useEffect } from 'react';
import useCaroGame from '../hooks/useCaroGame';
import CaroBoard from './CaroBoard';
import GameSidebar from './GameSidebar';
import ChatPopup from './ChatPopup';
import ChatIcon from './ChatIcon';
import MobileGameNav from './MobileGameNav';

const CaroGame = ({ gameData, onNavigate }) => {
  const {
    gameState,
    connectionStatus,
    error,
    playerColor,
    currentTurn,
    gameStatus,
    players,
    chatMessages,
    boardSize,
    winCondition,
    lastMove,
    makeMove,
    sendChatMessage,
    resign,
    connectToRoom
  } = useCaroGame();

  const [isInitialized, setIsInitialized] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [lastReadMessageCount, setLastReadMessageCount] = useState(0);

  // Initialize connection when component mounts
  useEffect(() => {
    if (gameData && !isInitialized) {
      connectToRoom(gameData.roomId, gameData.playerName);
      setIsInitialized(true);
    }
  }, [gameData, isInitialized, connectToRoom]);

  // Track unread messages
  useEffect(() => {
    if (!isChatOpen) {
      setUnreadMessages(chatMessages.length - lastReadMessageCount);
    }
  }, [chatMessages.length, lastReadMessageCount, isChatOpen]);

  const handleChatToggle = () => {
    setIsChatOpen(!isChatOpen);
    if (!isChatOpen) {
      setLastReadMessageCount(chatMessages.length);
      setUnreadMessages(0);
    }
  };

  const handleBackToHome = () => {
    if (window.confirm('Are you sure you want to leave the game?')) {
      onNavigate('home');
    }
  };

  if (!gameData) {
    return (
      <div className="container">
        <div className="error">No game data provided</div>
      </div>
    );
  }

  if (connectionStatus === 'connecting') {
    return (
      <div className="container">
        <div className="loading-container">
          <div className="loading"></div>
          <h3>Connecting to game...</h3>
          <p>Room: {gameData.roomId}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="card">
          <div className="error">
            <h3>Connection Error</h3>
            <p>{error}</p>
            <button 
              className="primary-button"
              onClick={() => connectToRoom(gameData.roomId, gameData.playerName)}
            >
              Retry Connection
            </button>
            <button 
              className="secondary-button"
              onClick={handleBackToHome}
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="game-container">
      <MobileGameNav
        onBack={handleBackToHome}
        gameStatus={gameStatus}
        currentTurn={currentTurn}
        playerColor={playerColor}
        roomId={gameData.roomId}
        onToggleChat={handleChatToggle}
        unreadMessages={unreadMessages}
      />
      
      <div className="game-header">
        <button 
          className="back-button"
          onClick={handleBackToHome}
        >
          ← Back to Home
        </button>
        <div className="room-info">
          <h2>Room: {gameData.roomId}</h2>
          <div className="connection-status">
            <span className={`status-indicator ${connectionStatus}`}></span>
            {connectionStatus === 'connected' ? 'Connected' : 'Disconnected'}
          </div>
        </div>
        <ChatIcon 
          onClick={handleChatToggle}
          hasNewMessage={unreadMessages > 0}
          messageCount={unreadMessages}
        />
      </div>

      <div className="game-content">
        <div className="game-main">
          <CaroBoard
            gameState={gameState}
            playerColor={playerColor}
            currentTurn={currentTurn}
            onMove={makeMove}
            gameStatus={gameStatus}
            boardSize={boardSize}
            winCondition={winCondition}
            lastMove={lastMove}
          />
        </div>

        <GameSidebar
          players={players}
          currentTurn={currentTurn}
          gameStatus={gameStatus}
          playerColor={playerColor}
          onResign={resign}
          boardSize={boardSize}
          winCondition={winCondition}
        />
      </div>

      <ChatPopup
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
        chatMessages={chatMessages}
        onSendMessage={sendChatMessage}
        playerColor={playerColor}
        players={players}
      />
    </div>
  );
};

export default CaroGame;
